import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { candidatesAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { showcaseAPI } from '@/utils/api';
import { toast } from 'sonner';
import i18n from '@/i18n/config';
import { SectorPicker } from '@/components/SectorPicker';
import { CandidateDocuments } from '@/components/candidate/CandidateDocuments';
import AmbassadorPanel from '@/components/AmbassadorPanel';

export default function CandidateProfile() {
  const { t } = useTranslation();
  const { checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    location: '',
    profile_summary: '',
    skills: [],
    experience_years: '',
    education: [],
    work_experience: [],
    business_categories: []
  });

  const [skillInput, setSkillInput] = useState('');

  const [educationForm, setEducationForm] = useState({
    school: '', degree: '', year: '', description: ''
  });

  const [workForm, setWorkForm] = useState({
    company: '', position: '', start_date: '', end_date: '', description: '', current: false
  });

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const { data } = await candidatesAPI.getMe();
      setProfile(data);
      setFormData({
        full_name: data.full_name || '',
        phone: data.phone || '',
        location: data.location || '',
        profile_summary: data.profile_summary || '',
        skills: data.skills || [],
        experience_years: data.experience_years || '',
        education: data.education || [],
        work_experience: data.work_experience || [],
        business_categories: data.business_categories || []
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        ...formData,
        experience_years:
          formData.experience_years === '' || formData.experience_years === undefined
            ? null
            : Number(formData.experience_years),
      };
      await candidatesAPI.updateProfile(payload);
      setMessage(t('candidateProfile.profileUpdated'));
      await checkAuth();
      await loadProfile();
    } catch (error) {
      setMessage(t('candidateProfile.errorUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUploaded = (config, data) => {
    const url = data[config.responseField || config.field];
    setProfile(current => ({
      ...current,
      [config.field]: config.multiple ? [...new Set([...(current?.[config.field] || []), url])] : url,
      ...(data.profile_completeness !== undefined ? { profile_completeness: data.profile_completeness } : {}),
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };
  const removeSkill = (skill) => setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });

  const addEducation = () => {
    if (educationForm.school && educationForm.degree) {
      setFormData({ ...formData, education: [...formData.education, { ...educationForm }] });
      setEducationForm({ school: '', degree: '', year: '', description: '' });
    }
  };
  const removeEducation = (index) => setFormData({ ...formData, education: formData.education.filter((_, i) => i !== index) });

  const addWorkExperience = () => {
    if (workForm.company && workForm.position) {
      setFormData({ ...formData, work_experience: [...formData.work_experience, { ...workForm }] });
      setWorkForm({ company: '', position: '', start_date: '', end_date: '', description: '', current: false });
    }
  };
  const removeWorkExperience = (index) => setFormData({ ...formData, work_experience: formData.work_experience.filter((_, i) => i !== index) });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{t('candidateProfile.pageTitle')}</title>
        <meta name="description" content={t('candidateProfile.pageDescription')} />
        <link rel="canonical" href="https://trabadju.cv/candidate/profile" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
            {t('candidateProfile.areaLabel')}
          </p>
          <h1 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tighter text-slate-900 leading-[1.1] mb-3">
            {t('candidateProfile.title')}
          </h1>
          <p className="text-[15px] text-slate-600 max-w-2xl leading-[1.6]">
            {t('candidateProfile.subtitle')}
          </p>
        </header>

        {/* Smart progress card */}
        {(() => {
          const checks = [
            { key: 'basic',  label: t('candidateProfile.checkBasic'),  done: !!(formData.full_name && formData.phone && formData.location) },
            { key: 'cv',     label: t('candidateProfile.checkCv'),     done: !!profile?.cv_url, primary: true },
            { key: 'exp',    label: t('candidateProfile.checkExp'),    done: (formData.work_experience || []).length > 0 },
            { key: 'edu',    label: t('candidateProfile.checkEdu'),    done: (formData.education || []).length > 0 },
            { key: 'skills', label: t('candidateProfile.checkSkills'), done: (formData.skills || []).length >= 3 },
            { key: 'sectors', label: t('candidateProfile.checkSectors'), done: (formData.business_categories || []).length > 0, boost: true },
          ];
          const completed = checks.filter((c) => c.done).length;
          const total = checks.length;
          const pct = Math.round((completed / total) * 100);
          const isFull = completed === total;
          return (
            <Card className="p-6 sm:p-7 mb-8 border-slate-200" data-testid="profile-progress-card">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase text-slate-500 mb-1">{t('candidateProfile.profileStatus')}</p>
                  <h2 className="text-[20px] sm:text-[22px] font-bold tracking-tight text-slate-900 leading-tight">
                    {isFull ? t('candidateProfile.profileComplete') : pct >= 60 ? t('candidateProfile.almostThere') : t('candidateProfile.letsComplete')}
                  </h2>
                </div>
                <div className="text-right flex-none">
                  <p className="text-[28px] sm:text-[32px] font-bold tracking-tighter text-slate-900 leading-none">{pct}%</p>
                  <p className="text-[11px] text-slate-500 mt-1 font-medium">{t('candidateProfile.ofTotal', { completed, total })}</p>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isFull ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <ul className="space-y-2.5" data-testid="profile-checklist">
                {checks.map((c) => (
                  <li key={c.key} className="flex items-start gap-3 text-[14px]" data-testid={`check-${c.key}`}>
                    <span
                      aria-hidden="true"
                      className={`flex-none mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                        c.done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}
                    >
                      {c.done ? '✓' : ''}
                    </span>
                    <span className={`flex-1 ${c.done ? 'text-slate-700 line-through decoration-slate-300' : 'text-slate-900 font-medium'}`}>
                      {c.label}
                      {c.boost && (
                        <span className={`ml-2 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${c.done ? 'text-emerald-700 bg-emerald-50' : 'text-fuchsia-700 bg-fuchsia-50'}`} data-testid="check-sectors-boost">
                          {t('candidateProfile.visibilityBoost')}
                        </span>
                      )}
                      {c.primary && !c.done && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          {t('candidateProfile.recommended')}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-slate-600" data-testid="profile-registration-complete-note">{t('candidateProfile.upload.accountReady')}</p>
            </Card>
          );
        })()}

        {message && (
          <Alert className="mb-6">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {/* Showcase opt-in/out */}
        {profile && (
          <Card className="p-5 mb-6 border-blue-100 bg-blue-50/40" data-testid="showcase-privacy-card">
            <div className="flex items-start gap-4">
              <div className="flex-none w-11 h-11 rounded-full bg-white border border-blue-200 text-blue-700 flex items-center justify-center">
                {profile.showcase_enabled === false ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">
                  {t('candidateProfile.showcaseTitle')}
                </h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  {t('candidateProfile.showcaseBody')}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button
                    variant={profile.showcase_enabled === false ? 'outline' : 'default'}
                    className={`max-w-full whitespace-normal h-auto min-h-11 py-2 ${profile.showcase_enabled === false ? '' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    onClick={async () => {
                      try {
                        const next = profile.showcase_enabled === false;
                        await showcaseAPI.updateSelf({ showcase_enabled: next });
                        toast.success(next ? t('candidateProfile.showcaseNowVisible') : t('candidateProfile.showcaseHidden'));
                        await loadProfile();
                      } catch {
                        toast.error(t('candidateProfile.errorUpdatePrefs'));
                      }
                    }}
                    data-testid="btn-toggle-showcase"
                  >
                    {profile.showcase_enabled === false ? t('candidateProfile.appearInShowcase') : t('candidateProfile.currentlyVisible')}
                  </Button>
                  {profile.showcase_enabled !== false && (
                    <Button
                      variant="ghost"
                      className="text-slate-500 hover:text-slate-900"
                      onClick={async () => {
                        try {
                          await showcaseAPI.updateSelf({ showcase_enabled: false });
                          toast.success(t('candidateProfile.showcaseHidden'));
                          await loadProfile();
                        } catch {
                          toast.error(t('candidateProfile.errorUpdateGeneric'));
                        }
                      }}
                      data-testid="btn-hide-showcase"
                    >
                      {t('candidateProfile.hide')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">{t('candidateProfile.basicInfo')}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>{t('candidateProfile.email')} <span className="text-xs text-gray-500 font-normal">({t('candidateProfile.emailHint')})</span></Label>
              <Input
                value={profile?.email || ''}
                readOnly
                disabled
                className="bg-gray-50 cursor-not-allowed"
                data-testid="profile-email-readonly"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('candidateProfile.emailBody')} <a href="mailto:info@trabadju.cv" className="text-blue-600 hover:underline">info@trabadju.cv</a>.
              </p>
            </div>
            <div>
              <Label>{t('candidateProfile.fullName')}</Label>
              <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} data-testid="candidate-profile-name" />
            </div>
            <div>
              <Label>{t('candidateProfile.phone')}</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+238 ..." data-testid="candidate-profile-phone" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('candidateProfile.location')}</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Praia, Santiago" data-testid="candidate-profile-location" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('candidateProfile.profileSummary')}</Label>
              <Textarea
                data-testid="candidate-profile-summary"
                value={formData.profile_summary}
                onChange={(e) => setFormData({ ...formData, profile_summary: e.target.value })}
                rows={4}
                placeholder={t('candidateProfile.profileSummaryPlaceholder')}
              />
            </div>
          </div>
        </Card>

        <CandidateDocuments profile={profile} onUploaded={handleDocumentUploaded} />

        <Card className="p-6 mb-6" data-testid="sectors-card">
          <h3 className="text-xl font-semibold mb-1">{t('candidateProfile.sectors')}</h3>
          <SectorPicker value={formData.business_categories} onChange={(v) => setFormData({ ...formData, business_categories: v })} />
        </Card>

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">{t('candidateProfile.skills')}</h3>
          <div className="flex gap-2 mb-3">
            <Input
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addSkill()}
              placeholder="Ex: React, Python, Marketing..."
            />
            <Button onClick={addSkill}>{t('candidateProfile.add')}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.skills.map((skill, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                {skill}
                <button onClick={() => removeSkill(skill)} className="hover:text-blue-900">×</button>
              </span>
            ))}
          </div>
        </Card>

        {/* EDUCATION */}
        <Card className="p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">{t('candidateProfile.education')}</h3>

          {formData.education.length > 0 && (
            <div className="mb-6 space-y-4">
              {formData.education.map((edu, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{edu.degree}</h4>
                      <p className="text-gray-700">{edu.school}</p>
                      <p className="text-gray-600 text-sm">{edu.year}</p>
                      {edu.description && <p className="text-gray-600 text-sm mt-2">{edu.description}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-2 border-dashed rounded-lg p-4">
            <h4 className="font-medium mb-3">{t('candidateProfile.addEducation')}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t('candidateProfile.school')} *</Label>
                <Input value={educationForm.school} onChange={(e) => setEducationForm({ ...educationForm, school: e.target.value })} placeholder="Ex: Universidade de Cabo Verde" />
              </div>
              <div>
                <Label>{t('candidateProfile.degree')} *</Label>
                <Input value={educationForm.degree} onChange={(e) => setEducationForm({ ...educationForm, degree: e.target.value })} placeholder="Ex: Licenciatura em Informática" />
              </div>
              <div>
                <Label>{t('candidateProfile.completionYear')}</Label>
                <Input value={educationForm.year} onChange={(e) => setEducationForm({ ...educationForm, year: e.target.value })} placeholder="Ex: 2020" />
              </div>
              <div>
                <Label>{t('candidateProfile.descriptionOptional')}</Label>
                <Input value={educationForm.description} onChange={(e) => setEducationForm({ ...educationForm, description: e.target.value })} placeholder={t('candidateProfile.briefDescription')} />
              </div>
            </div>
            <Button onClick={addEducation} className="mt-4" variant="outline">
              <Plus className="h-4 w-4 mr-2" />{t('candidateProfile.addEducation')}
            </Button>
          </div>
        </Card>

        {/* WORK EXPERIENCE */}
        <Card className="p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">{t('candidateProfile.workExperience')}</h3>

          <div className="mb-4">
            <Label>{t('candidateProfile.totalYearsExp')}</Label>
            <Input
              type="number"
              value={formData.experience_years}
              onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || '' })}
              placeholder="Ex: 5"
              className="max-w-xs"
            />
          </div>

          {formData.work_experience.length > 0 && (
            <div className="mb-6 space-y-4">
              {formData.work_experience.map((work, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{work.position}</h4>
                      <p className="text-gray-700">{work.company}</p>
                      <p className="text-gray-600 text-sm">
                        {work.start_date} - {work.current ? t('candidateProfile.present') : work.end_date}
                      </p>
                      {work.description && <p className="text-gray-600 text-sm mt-2">{work.description}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeWorkExperience(index)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-2 border-dashed rounded-lg p-4">
            <h4 className="font-medium mb-3">{t('candidateProfile.addExperience')}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t('candidateProfile.company')} *</Label>
                <Input value={workForm.company} onChange={(e) => setWorkForm({ ...workForm, company: e.target.value })} placeholder="Ex: Tech Solutions CV" />
              </div>
              <div>
                <Label>{t('candidateProfile.position')} *</Label>
                <Input value={workForm.position} onChange={(e) => setWorkForm({ ...workForm, position: e.target.value })} placeholder="Ex: Desenvolvedor Web" />
              </div>
              <div>
                <Label>{t('candidateProfile.startDate')}</Label>
                <Input value={workForm.start_date} onChange={(e) => setWorkForm({ ...workForm, start_date: e.target.value })} placeholder="Ex: Jan 2020" />
              </div>
              <div>
                <Label>{t('candidateProfile.endDate')}</Label>
                <Input value={workForm.end_date} onChange={(e) => setWorkForm({ ...workForm, end_date: e.target.value })} placeholder="Ex: Dez 2023" disabled={workForm.current} />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={workForm.current} onChange={(e) => setWorkForm({ ...workForm, current: e.target.checked, end_date: '' })} className="h-4 w-4" />
                  <Label>{t('candidateProfile.currentJob')}</Label>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>{t('candidateProfile.responsibilitiesDescription')}</Label>
                <Textarea value={workForm.description} onChange={(e) => setWorkForm({ ...workForm, description: e.target.value })} rows={3} placeholder={t('candidateProfile.responsibilitiesPlaceholder')} />
              </div>
            </div>
            <Button onClick={addWorkExperience} className="mt-4" variant="outline">
              <Plus className="h-4 w-4 mr-2" />{t('candidateProfile.addExperience')}
            </Button>
          </div>
        </Card>

        <div className="flex justify-end gap-4 pb-32 sm:pb-28">
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700 px-8" data-testid="save-profile-inline-btn">
            {saving ? t('candidateProfile.saving') : t('candidateProfile.saveProfile')}
          </Button>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_12px_rgba(15,23,42,0.06)]" data-testid="sticky-save-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <p className="text-[13px] text-slate-600 hidden sm:block">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-2 align-middle" />
            <span dangerouslySetInnerHTML={{ __html: t('candidateProfile.changesSavedNote') }} />
          </p>
          <p className="text-[12px] text-slate-600 sm:hidden">{t('candidateProfile.dontForgetSave')}</p>
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6 font-semibold rounded-lg" data-testid="save-profile-sticky-btn">
            {saving ? t('candidateProfile.saving') : t('candidateProfile.saveProfileShort')}
          </Button>
        </div>
      </div>

      {/* Ambassador Panel — fixed above save bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-36 mt-8">
        <AmbassadorPanel />
      </div>
    </main>
  );
}
